import React, { useMemo, useRef } from "react";
import GenericApi from "@/api/genericApi";
import {
  CryptoDepositPostQueryKey,
  TransactionQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse } from "@/types/Api";
import { useQuery } from "@tanstack/react-query";
import { Transaction } from "@/types/Transaction";
import Loader from "../Loader";
import { CryptoDepositPost } from "@/types/BankSettings";
import { TransactionStatus } from "@/types/Status.tsx";
import { getUserToken } from "@/utils/localStorage";
import axios from "axios";
import { isBitramp } from "@/utils/utils";

interface ActivityTableReportProps {
  isReport: boolean;
}

const ActivityTableReport: React.FC<ActivityTableReportProps> = ({
  isReport,
}) => {
  const reportRef = useRef<HTMLDivElement>(null);

  const formatDate = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  const formatHour = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Adaugă zero înainte pentru a avea două cifre
    const day = String(today.getDate()).padStart(2, "0"); // Adaugă zero înainte pentru a avea două cifre
    return `${year}-${month}-${day}`;
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Scade o zi din data curentă
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, "0");
    const day = String(yesterday.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data: transactionsToday, isLoading: isLoadingTransactions } =
    useQuery<ApiResponse<Transaction[]>>({
      queryFn: async () => {
        const today = getTodayDate();
        const yesterday = getYesterdayDate();
        return GenericApi.get<Transaction[]>(
          `${ApiRoutes.ONEDAYTRANSACTIONS}?start_date=${yesterday}&end_date=${today}`
        );
      },
      queryKey: [TransactionQueryKey],
    });

  const { data: depositsToday, isLoading: isLoadingDeposits } = useQuery<
    ApiResponse<CryptoDepositPost[]>
  >({
    queryFn: async () => {
      const today = getTodayDate();
      const yesterday = getYesterdayDate();
      return GenericApi.get<CryptoDepositPost[]>(
        `${ApiRoutes.CRYPTODEPOSITLIST}?start_date=${yesterday}&end_date=${today}`
      );
    },
    queryKey: [CryptoDepositPostQueryKey],
  });

  const combinedData = useMemo(() => {
    if (!transactionsToday || !depositsToday) return [];
    return [
      ...(transactionsToday?.data || []),
      ...(depositsToday?.data || []),
    ].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [transactionsToday, depositsToday]);

  const handleTransactionsDownload = async () => {
    try {
      const today = getTodayDate();
      const yesterday = getYesterdayDate();
      const response = await axios.get(
        `${GenericApi.baseUrl}/statistics/transactions/download?start_date=${yesterday}&end_date=${today}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `invoice_transactions.xlsx`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading EXCEL:", error);
    }
  };

  const handlePOSTransactionsDownload = async () => {
    try {
      const today = getTodayDate();
      const yesterday = getYesterdayDate();
      const response = await axios.get(
        `${GenericApi.baseUrl}/statistics/pos-transactions/download?start_date=${yesterday}&end_date=${today}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `invoice_pos_transactions.xlsx`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading EXCEL:", error);
    }
  };

  const handleDepositWithdrawsDownload = async () => {
    try {
      const today = getTodayDate();
      const yesterday = getYesterdayDate();
      const response = await axios.get(
        `${GenericApi.baseUrl}/statistics/direct-transactions/download?start_date=${yesterday}&end_date=${today}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `invoice_deposit+withdraw.xlsx`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  if (isLoadingTransactions || isLoadingDeposits) {
    return <Loader />;
  }

  return (
    <div
      className=" py-6 px-8 font-['Satoshi'] mb-5 tableContainer Container pshadow"
      ref={reportRef}
    >
      <h2 className="Title bold dark-blue mb-6">Recent Transactions</h2>
      {isReport ? (
        <div className="flex gap-4 pb-6">
          <button
            onClick={handleTransactionsDownload}
            className={`${
              isBitramp() ? "Upload_button_bitramp" : "Primary_button"
            }  w-fit mt-4 `}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="18"
              viewBox="0 0 14 18"
              fill="none"
            >
              <g clip-path="url(#clip0_3058_8281)">
                <path
                  d="M14.0002 8.01575L6.9361 14.6214L0.00168819 7.93497C-0.0543177 7.69687 1.30014 6.61413 1.54038 6.3321L5.8322 10.2649V0H8.16676V10.2649L12.3348 6.3321L14.0017 7.73372V8.01433L14.0002 8.01575Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
                <path
                  d="M13.4177 15.75H0.582031V18.0005H13.4177V15.75Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
              </g>
              <defs>
                <clipPath id="clip0_3058_8281">
                  <rect width="14" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <div className={`${isBitramp() ? "text-[#262626]" : ""} `}>
              Download Transactions Excel
            </div>
          </button>
          <button
            onClick={handlePOSTransactionsDownload}
            className={`${
              isBitramp() ? "Upload_button_bitramp" : "Primary_button"
            }  w-fit mt-4 `}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="18"
              viewBox="0 0 14 18"
              fill="none"
            >
              <g clip-path="url(#clip0_3058_8281)">
                <path
                  d="M14.0002 8.01575L6.9361 14.6214L0.00168819 7.93497C-0.0543177 7.69687 1.30014 6.61413 1.54038 6.3321L5.8322 10.2649V0H8.16676V10.2649L12.3348 6.3321L14.0017 7.73372V8.01433L14.0002 8.01575Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
                <path
                  d="M13.4177 15.75H0.582031V18.0005H13.4177V15.75Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
              </g>
              <defs>
                <clipPath id="clip0_3058_8281">
                  <rect width="14" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <div className={`${isBitramp() ? "text-[#262626]" : ""} `}>
              Download POS Transactions Excel
            </div>
          </button>
          <button
            onClick={handleDepositWithdrawsDownload}
            className={`${
              isBitramp() ? "Upload_button_bitramp" : "Primary_button"
            }  w-fit mt-4 `}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="18"
              viewBox="0 0 14 18"
              fill="none"
            >
              <g clip-path="url(#clip0_3058_8281)">
                <path
                  d="M14.0002 8.01575L6.9361 14.6214L0.00168819 7.93497C-0.0543177 7.69687 1.30014 6.61413 1.54038 6.3321L5.8322 10.2649V0H8.16676V10.2649L12.3348 6.3321L14.0017 7.73372V8.01433L14.0002 8.01575Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
                <path
                  d="M13.4177 15.75H0.582031V18.0005H13.4177V15.75Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
              </g>
              <defs>
                <clipPath id="clip0_3058_8281">
                  <rect width="14" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <div className={`${isBitramp() ? "text-[#262626]" : ""} `}>
              Download Deposit/Withdraw Excel
            </div>
          </button>
        </div>
      ) : null}
      <div
        className={`${
          isBitramp() ? "crypto-list_bitramp" : ""
        } overflow-y-auto max-h-[70vh] tableContainer--background px-1`}
      >
        <table className="w-full border-collapse relative Text">
          <thead
            className={`sticky top-0 tableContainer__header ${
              isBitramp() ? "!bg-[#EE33D9]" : ""
            }`}
          >
            <tr>
              <th
                className={`tableContainer__header__row white ${
                  isBitramp() ? "!bg-[#EE33D9]" : ""
                }`}
              >
                Action
              </th>
              <th
                className={`tableContainer__header__row white ${
                  isBitramp() ? "!bg-[#EE33D9]" : ""
                }`}
              >
                Amount
              </th>
              <th
                className={`tableContainer__header__row white ${
                  isBitramp() ? "!bg-[#EE33D9]" : ""
                }`}
              >
                Purchased
              </th>
              <th
                className={`tableContainer__header__row white ${
                  isBitramp() ? "!bg-[#EE33D9]" : ""
                }`}
              >
                Date
              </th>
              <th
                className={`tableContainer__header__row white ${
                  isBitramp() ? "!bg-[#EE33D9]" : ""
                }`}
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {combinedData.map((transaction, index) => {
              const amount = Number(transaction.amount);
              const isEven = index % 2 === 1;

              return (
                <React.Fragment key={transaction.id}>
                  <tr>
                    <td
                      className={`uppercase bold tableContainer__body__row py-2 rounded-l-[0.625rem] ${
                        isBitramp() ? "text-[#fff]" : ""
                      } ${
                        isEven
                          ? isBitramp()
                            ? "bg-[#464646]"
                            : "par"
                          : "impar"
                      }`}
                    >
                      {`action` in transaction
                        ? transaction.action
                        : (transaction as Transaction).type}
                    </td>
                    <td
                      className={`tableContainer__body__row ${
                        isBitramp() ? "text-[#fff]" : ""
                      } ${
                        isEven
                          ? isBitramp()
                            ? "bg-[#464646]"
                            : "par"
                          : "impar"
                      }`}
                    >
                      {transaction.amount.toString().slice(0, 15)}{" "}
                      <span className="low_caps">
                        {`rate` in transaction
                          ? transaction.rate.from_currency_symbol
                          : transaction.currency}
                      </span>
                    </td>
                    <td
                      className={`tableContainer__body__row ${
                        isBitramp() ? "text-[#fff]" : ""
                      } ${
                        isEven
                          ? isBitramp()
                            ? "bg-[#464646]"
                            : "par"
                          : "impar"
                      }`}
                    >
                      {(`rate` in transaction
                        ? transaction.rate.rate * amount
                        : 0
                      ).toFixed(2)}{" "}
                      <span className="low_caps">
                        {`rate` in transaction
                          ? transaction.rate.to_currency_symbol
                          : ""}
                      </span>
                    </td>
                    <td
                      className={`low_caps tableContainer__body__row ${
                        isBitramp() ? "text-[#fff]" : ""
                      } ${
                        isEven
                          ? isBitramp()
                            ? "bg-[#464646]"
                            : "par"
                          : "impar"
                      }`}
                    >
                      {formatDate(transaction.created_at)}
                      <span className="text-[#b3b2b1] ml-2">
                        {formatHour(transaction.created_at)}
                      </span>
                    </td>
                    <td
                      style={{
                        color:
                          transaction?.status === TransactionStatus.COMPLETED
                            ? "#2DDE24"
                            : "#F00",
                      }}
                      className={`low_caps tableContainer__body__row rounded-r-[0.625rem] ${
                        isBitramp() ? "text-[#fff]" : ""
                      } ${
                        isEven
                          ? isBitramp()
                            ? "bg-[#464646]"
                            : "par"
                          : "impar"
                      }`}
                    >
                      {transaction?.status ?? "N/A"}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityTableReport;
